import type { FCWithChildren } from '@pbk-web/types';
import { createContext, useContext } from 'react';

/**
 * How editors store and re-hydrate images.
 * - `upload` persists a blob and returns a displayable URL plus the id saved as `data-file-id`.
 * - `resolve` turns a saved id back into a fresh (often short-lived) URL on load.
 */
export type ImageUploadAdapter = {
  upload: (blob: Blob) => Promise<{ url: string; fileId: string }>;
  resolve?: (fileId: string) => Promise<string>;
};

/** App services every editor uses, supplied by the host so this lib stays free of the data layer. */
export type LexicalIntegrations = {
  images?: ImageUploadAdapter;
  /**
   * Called for every link click inside an editor, with the clicked anchor as `event.target`.
   * - Call `event.preventDefault()` to stop the editor opening it (e.g. to confirm an external link first).
   */
  onLinkClick?: (event: MouseEvent) => void;
};

const IntegrationsContext = createContext<LexicalIntegrations>({});

/** App-wide editor services; swap a value here to change it for every editor. */
export const LexicalIntegrationsProvider: FCWithChildren<{ value: LexicalIntegrations }> = ({ value, children }) => (
  <IntegrationsContext.Provider value={value}>{children}</IntegrationsContext.Provider>
);

export const useIntegrations = (): LexicalIntegrations => useContext(IntegrationsContext);
